/**
 * estimateJobSpecLabor — JobSpec → Estimated Internal Labor Cost (PR4)
 * ────────────────────────────────────────────────────────────────────
 * Pure derivation. Converts a bound JobSpec's phase footprint into a
 * `LaborCostEstimateInput` consumable by `getJobPayRun()`.
 *
 * Why a phase-name table (not a field on JobSpec):
 *   • JobSpec authoring intentionally does NOT edit phase count/name/order
 *     (see jobSpecAuthoring header). Adding `estimatedHours` to JobSpecPhase
 *     would force a registry migration and mutate frozen BoundSpecSnapshots.
 *   • The phase set is small (prep / demo / plumbing / install / qa) and
 *     stable. A standard-hours-per-phase table tuned per archetype is
 *     sufficient for beta variance reporting.
 *
 * Beta hours table is deliberately conservative. Real orgs will tune
 * via a future settings surface; consumers ALWAYS route through this
 * resolver — never hardcode hours inline.
 *
 * NEVER reads or returns customer pricing. Labor cost only.
 */

import type { BoundJobSpec, JobSpecPhaseName } from "@/data/jobSpec";
import type { SpecArchetype } from "@/data/contractorPayProfile";
import { getLoadedRate, type InternalCrewRole } from "./internalLaborRates";
import type { LaborCostEstimateInput } from "./getJobPayRun";

/** Per-phase, per-role hours. Beta defaults tuned for bath remodels. */
const PHASE_HOURS_BY_ARCHETYPE: Record<
  SpecArchetype | "default",
  Partial<Record<JobSpecPhaseName, Array<{ role: InternalCrewRole; hours: number }>>>
> = {
  "TTS-STD": {
    prep:     [{ role: "crew_lead", hours: 1 }, { role: "installer", hours: 1 }],
    demo:     [{ role: "installer", hours: 3 }, { role: "helper", hours: 3 }],
    plumbing: [{ role: "plumber", hours: 2 }],
    install:  [{ role: "crew_lead", hours: 5 }, { role: "installer", hours: 5 }],
    qa:       [{ role: "crew_lead", hours: 1 }],
  },
  "SHWR-RPL": {
    prep:     [{ role: "crew_lead", hours: 1 }, { role: "installer", hours: 1 }],
    demo:     [{ role: "installer", hours: 4 }, { role: "helper", hours: 4 }],
    plumbing: [{ role: "plumber", hours: 3 }],
    install:  [{ role: "crew_lead", hours: 6 }, { role: "installer", hours: 6 }, { role: "tile_setter", hours: 4 }],
    qa:       [{ role: "crew_lead", hours: 1 }],
  },
  "TUB-RPL": {
    prep:     [{ role: "crew_lead", hours: 1 }],
    demo:     [{ role: "installer", hours: 3 }, { role: "helper", hours: 2 }],
    plumbing: [{ role: "plumber", hours: 2 }],
    install:  [{ role: "crew_lead", hours: 4 }, { role: "installer", hours: 4 }],
    qa:       [{ role: "crew_lead", hours: 1 }],
  },
  "WALK-IN-TUB": {
    prep:     [{ role: "crew_lead", hours: 1 }, { role: "installer", hours: 1 }],
    demo:     [{ role: "installer", hours: 4 }, { role: "helper", hours: 3 }],
    plumbing: [{ role: "plumber", hours: 4 }],
    install:  [{ role: "crew_lead", hours: 6 }, { role: "installer", hours: 6 }, { role: "electrician", hours: 2 }],
    qa:       [{ role: "crew_lead", hours: 1 }],
  },
  "FULL-BATH": {
    prep:     [{ role: "crew_lead", hours: 2 }, { role: "installer", hours: 2 }],
    demo:     [{ role: "installer", hours: 6 }, { role: "helper", hours: 6 }],
    plumbing: [{ role: "plumber", hours: 4 }],
    install:  [{ role: "crew_lead", hours: 10 }, { role: "installer", hours: 10 }, { role: "tile_setter", hours: 8 }, { role: "electrician", hours: 2 }],
    qa:       [{ role: "crew_lead", hours: 2 }],
  },
  "WET-SPACE": {
    prep:     [{ role: "crew_lead", hours: 1 }],
    demo:     [{ role: "installer", hours: 4 }, { role: "helper", hours: 3 }],
    plumbing: [{ role: "plumber", hours: 3 }],
    install:  [{ role: "crew_lead", hours: 6 }, { role: "tile_setter", hours: 6 }],
    qa:       [{ role: "crew_lead", hours: 1 }],
  },
  "WINDOW": {
    prep:     [{ role: "installer", hours: 1 }],
    install:  [{ role: "installer", hours: 3 }, { role: "helper", hours: 2 }],
    qa:       [{ role: "crew_lead", hours: 1 }],
  },
  default: {
    prep:     [{ role: "crew_lead", hours: 1 }, { role: "installer", hours: 1 }],
    demo:     [{ role: "installer", hours: 3 }],
    plumbing: [{ role: "plumber", hours: 2 }],
    install:  [{ role: "crew_lead", hours: 5 }, { role: "installer", hours: 5 }],
    qa:       [{ role: "crew_lead", hours: 1 }],
  },
};

function tableFor(archetype?: SpecArchetype) {
  return PHASE_HOURS_BY_ARCHETYPE[archetype ?? "default"] ?? PHASE_HOURS_BY_ARCHETYPE.default;
}

export interface JobSpecLaborEstimate extends LaborCostEstimateInput {
  /** Total estimated hours across all phases/roles. */
  totalHours: number;
  /** Per-phase rollup for UI display. */
  byPhase: Array<{ phase: JobSpecPhaseName; hours: number; dollars: number }>;
}

/**
 * Derives an internal-labor cost estimate from a bound JobSpec. Returns
 * `null` when the job has no bound spec (caller should not attempt to
 * estimate internal labor without a spec).
 */
export function estimateJobSpecLabor(
  boundSpec: BoundJobSpec | undefined,
): JobSpecLaborEstimate | null {
  if (!boundSpec?.snapshot) return null;
  const archetype = boundSpec.snapshot.archetype;
  const table = tableFor(archetype);

  const byRole: Array<{ role: string; hours: number; loadedRate: number }> = [];
  const roleAccum = new Map<InternalCrewRole, number>();
  const byPhase: JobSpecLaborEstimate["byPhase"] = [];
  let totalHours = 0;
  let estimatedDollars = 0;

  for (const specPhase of boundSpec.snapshot.phases) {
    const entries = table[specPhase.name] ?? [];
    let phaseHours = 0;
    let phaseDollars = 0;
    for (const entry of entries) {
      const rate = getLoadedRate(entry.role);
      const dollars = entry.hours * rate;
      phaseHours += entry.hours;
      phaseDollars += dollars;
      roleAccum.set(entry.role, (roleAccum.get(entry.role) ?? 0) + entry.hours);
    }
    totalHours += phaseHours;
    estimatedDollars += phaseDollars;
    byPhase.push({ phase: specPhase.name, hours: phaseHours, dollars: phaseDollars });
  }

  for (const [role, hours] of roleAccum) {
    byRole.push({ role, hours, loadedRate: getLoadedRate(role) });
  }

  return { estimatedDollars, byRole, totalHours, byPhase };
}
